/* eslint-disable react/no-array-index-key */
import { useContact } from '@/context/Contact';
import { MarketoForm, useMarketo } from '@/context/Marketo';
import { usePageModels } from '@/context/PageModels';
import { datalayerPush, withGTM } from '@/features/shared/HOCs/withGTM';
import { MuiCircularProgress, MuiTypography } from '@/features/shared/material-ui';
import MessageBar from '@/features/shared/MessageBar';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { ColorNames, IS_LOCAL, MarketoControlsDistribution, Theme } from '@/utils/constants';
import { isEmail } from '@/utils/helpers';
import clsx from 'clsx';
import { debounce } from 'lodash';
import React, {
  createRef,
  FocusEvent,
  FormEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { areEqual } from 'react-window';
import { MarketoReCAPTCHA, MarketoRow } from './Controls';
import { onDatalayerCustomSubmit, useMarketoFormDatalayer } from './MarketoForm.datalayer';
import {
  advancedLogicEmptyFieldsValue,
  advancedLogicFacade,
  advancedLogicFilterFields,
} from './MarketoForm.facade';
import { MarketoFormProps } from './MarketoForm.interface';
import useStyles from './MarketoForm.styles';
import { mapControls } from './MarketoForm.utils';
import { useCRMSync } from './hooks/useCRMSync';
import { useEmailBlacklist } from './hooks/useEmailBlacklist';
import { getFilteredOneTrustActiveGroups } from './utils';
import { useMarketoFormAppearance } from '@/features/MarketoForm/hooks/useMarketoFormAppearance';
import { useAppearanceTheme, useMarketoLogic } from '@/features/MarketoForm/MarketoForm.logic';
import { useMutationObserver } from '@/hooks/useObserveMutation';

const SELECT_FORM_CONTROL_PREFIX = 'mui-component-select-';
const CHECKBOX_FORM_CONTROL_PREFIX = 'mktoCheckbox';

declare const window: any;

export const MarketoFormComponent = ({
  heading,
  formDescription,
  displayMessages = true,
  confirmationMessage,
  formId,
  onSuccess,
  contentButtons,
  theme = Theme.light,
  rules,
  recaptchaSitekey,
  recaptchaLegal,
  outline = true,
  containerColor = ColorNames.noBackgroundColor,
  controlsDistribution = MarketoControlsDistribution.Default,
  emailBlacklistErrorMessage = '',
  emailFieldname = '',
  emailsBlacklisted = [],
  emailCRMFieldname = 'emailaddress1',
  CRMSyncFieldnames = [],
  className: overrideClassName = '',
  enableCRMSSync = false,
  setParentDataLayerFormAttributes,
  isOpenedByCTAVariant,
  datalayerLinkText,
  labels = {},
  appearance,
  isModalForm = false,
}: Br.PropsWithContentButtons<MarketoFormProps>) => {
  const { contactInfo } = useContact();
  const advancedRules = useMemo(() => JSON.parse(rules || '[]'), [rules]);
  const formLoading = useRef<boolean>(false);
  const formLoaded = useRef<boolean>(false);
  const [form, setForm] = React.useState<MarketoForm>();
  const classes = useStyles();
  const formRef = useRef<HTMLFormElement>(null);
  const marketoFormRef = useRef<HTMLFormElement>(null);
  const [controls, setControls] = useState<any[]>([]);
  const lastControls = useRef<any[]>([]);
  const [renderableControls, setRenderableControls] = useState<any[]>([]);
  const [emptyFields, setEmptyFields] = useState<any>({});
  const [value, setValue] = useState<any>({});
  const DEBOUNCE_TIMEOUT_MILISECONDS = useRef(500);
  const recaptchaRef = createRef<any>();
  const [messageBarOpen, setMessageBarOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [recaptchaFlag, setRecaptchaFlag] = React.useState<boolean>(false);
  const formAppearance = useMarketoFormAppearance(appearance);
  const { hasMarketoError } = useMarketoLogic();
  const submitTextRef = useRef();

  const { loadForm, isLoading } = useMarketo();
  const appearanceTheme = useAppearanceTheme(appearance, theme);

  const updateControls = React.useCallback((marketoForm?: MarketoForm) => {
    if (!marketoForm) return;

    const [formElement] = marketoForm.getFormElem();
    const nextControls = mapControls(formElement);
    if (areEqual(lastControls.current, nextControls)) return;

    setControls(nextControls);
    lastControls.current = nextControls;
    setValue((prevValue: any) => ({ ...prevValue, ...marketoForm.getValues() }));
  }, []);

  const onMarketoError = useCallback(
    (mutations: MutationRecord[]) => {
      if (hasMarketoError(mutations)) {
        updateControls(form);
      }
    },
    [updateControls, form, hasMarketoError],
  );

  // TODO (ben) improve it and use the updateControls with the observer
  useMutationObserver(marketoFormRef.current as HTMLElement, onMarketoError, {
    subtree: true,
    childList: true,
  });

  const resetForm = useCallback(
    (marketoForm?: MarketoForm, newValues: Record<string, any> = {}) => {
      if (!marketoForm) return;

      const formElement = marketoForm?.getFormElem()[0];
      formElement.reset();
      const marketoButton = formElement.querySelector('[type="submit"]');

      marketoForm?.setValues({});

      marketoButton.removeAttribute('disabled');
      marketoButton.innerHTML = submitTextRef.current;

      updateControls(marketoForm);
    },
    [updateControls],
  );

  const updateMarketoForm = useCallback(
    (newValues: Record<string, any> = {}) => {
      if (!form) return;

      form.setValues(newValues);

      updateControls(form);
    },
    [updateControls, form],
  );

  const {
    dataLayerFocusEvent,
    datalayerFormErrorEvent,
    datalayerClickToFormEvent,
    datalayerFormEngagementEvent,
  } = useMarketoFormDatalayer(formId, setParentDataLayerFormAttributes, containerRef);

  const handleSuccess = useCallback(
    (marketoForm?: MarketoForm, defaultValues?: Record<string, any>) => {
      resetForm(marketoForm, defaultValues);
      setMessageBarOpen(true);

      if (onSuccess) {
        onSuccess();
      }
    },
    [onSuccess, resetForm],
  );

  const handleCloseMessageBar = useCallback(() => {
    setMessageBarOpen(false);
  }, []);

  const handleFocusHiddenForm = useCallback((e: FocusEvent) => {
    const formElement = formRef.current?.querySelector(`#${e.target.id}_2`) as HTMLElement;

    if (formElement?.tabIndex === -1) {
      const siblingTabElement = formElement?.parentElement?.querySelector(
        '[role="button"]',
      ) as HTMLElement;

      if (!siblingTabElement) return;

      siblingTabElement.focus();

      return;
    }

    if (!formElement) return;

    formElement.focus();
  }, []);

  const { submitCRMSyncValues, syncCRMValues } = useCRMSync({
    CRMSyncFieldnames,
    emailFieldname,
    emailCRMFieldname,
  });

  // Load form
  React.useEffect(() => {
    if (isLoading) return;
    if (form || formLoading.current || formLoaded.current) return;
    if (!formId) return;
    if (!marketoFormRef.current) return;

    marketoFormRef.current.innerHTML = '';
    marketoFormRef.current.id = `mktoForm_${formId}`;

    formLoading.current = true;

    // We are using the setTimeout because this component mount and unmount multiple times
    // and using the delay we ensure to load the form with the last render
    // TODO: Review the component rendering becuase it causes to load the form more than one time
    window.loadFormProcess = setTimeout(() => {
      // console.log('[Mkto] loading form ', window.loadFormProcess);
      loadForm({ formId, onSuccess: handleSuccess }).then((marketoForm) => {
        if (!marketoFormRef.current || !marketoForm) return;

        marketoFormRef.current.id = '';

        syncCRMValues(marketoForm);

        setForm(marketoForm);
        formLoading.current = false;
        formLoaded.current = true;

        updateControls(marketoForm);
      });
    }, 1000); // waiting for 1 sec for having more time to clear the prev loadForm

    // console.log('[Mkto] init load form', window.loadFormProcess);

    return () => {
      if (!isModalForm) {
        // console.log('[Mkto] clear load form', window.loadFormProcess);
        clearTimeout(window.loadFormProcess);
      }
    };
  }, [
    form,
    formId,
    loadForm,
    handleSuccess,
    updateControls,
    syncCRMValues,
    isLoading,
    isModalForm,
  ]);

  const { errorMessageBarProps, verifyMail } = useEmailBlacklist({
    emailBlacklistErrorMessage,
    emailFieldname,
    emailsBlacklisted,
  });

  const handleMarketoSubmit = React.useCallback(async () => {
    if (!form) return;

    // Set as empty fields hidden fields (advanced logic).
    form.setValues({ ...emptyFields });
    // Set a customMktoReferrer that takes the current URL.
    form.addHiddenFields({
      customMktoReferrer: window.location.href,
      ...(!!window.OnetrustActiveGroups && {
        onetrustActiveGroups: getFilteredOneTrustActiveGroups(),
      }),
    });

    // Verify if the Email is blacklisted
    let isBlacklisted = false;
    const isFormValid = form.validate();
    let isFormSubmittable = form.submitable();
    const marketoFormValues = form.getValues();

    // Extra e-mail validation layer.
    const EmailEl = form.getFormElem().find(`#${emailFieldname}`);
    if (
      !!EmailEl?.length &&
      isFormValid &&
      isFormSubmittable &&
      !!marketoFormValues?.Email?.trim() &&
      !isEmail(marketoFormValues?.Email?.trim())
    ) {
      isFormSubmittable = false;

      form.showErrorMessage(
        labels?.email_error_message_validation || 'Must be valid email. example@yourdomain.com',
        EmailEl,
      );
    }

    if (isFormValid && isFormSubmittable) {
      isBlacklisted = verifyMail(marketoFormValues);
    }

    if (isFormValid && isFormSubmittable && !isBlacklisted) {
      if (enableCRMSSync) {
        await submitCRMSyncValues(form, marketoFormValues);
      }

      const submitButton = form.getFormElem()?.[0]?.querySelector('button[type="submit"]');
      submitTextRef.current = submitButton.innerHTML;
      try {
        form.submit();
      } catch (error) {
        submitButton.click();
      }

      const variantName = (
        containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement
      )?.dataset?.datalayerVariant;

      // legacy Datalayer
      datalayerPush({
        compRef: containerRef,
        event: 'gtm:custom:form:submit',
        args: {
          event: 'formSubmission',
          formId,
          variantName,
          ...marketoFormValues,
        },
      });

      // GA4 - Datalayer
      datalayerFormEngagementEvent(containerRef, marketoFormValues, variantName);
      setTimeout(() => updateControls(form), 100);
    }
  }, [
    form,
    emptyFields,
    emailFieldname,
    labels?.email_error_message_validation,
    verifyMail,
    enableCRMSSync,
    formId,
    submitCRMSyncValues,
    datalayerFormEngagementEvent,
    updateControls,
  ]);

  const handleSubmit = React.useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      if (!recaptchaSitekey || IS_LOCAL) {
        handleMarketoSubmit();
        return;
      }

      setRecaptchaFlag(false);
      recaptchaRef?.current?.reset();
      recaptchaRef?.current?.execute();
    },
    [handleMarketoSubmit, recaptchaRef, recaptchaSitekey],
  );

  React.useEffect(() => {
    if (!recaptchaFlag) return;

    handleMarketoSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptchaFlag]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedUpdateMarketoForm = useCallback(
    debounce(updateMarketoForm, DEBOUNCE_TIMEOUT_MILISECONDS.current),
    [updateMarketoForm],
  );

  const { marketoContactInfoPrefill = {} } = usePageModels();

  React.useEffect(() => {
    if (!form) return;
    if (!contactInfo) return;
    if (!Object.keys(contactInfo)?.length) return;
    if (!Object.keys(marketoContactInfoPrefill)?.length) return;

    try {
      const defaultValues = Object.keys(marketoContactInfoPrefill).reduce((acc, curr) => {
        return { ...acc, [curr]: contactInfo?.[marketoContactInfoPrefill[curr]] };
      }, {});
      form.setDefaultValues(defaultValues);
    } catch (error) {
      console.error(':: MarketoForm auto-fill error ', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactInfo, form]);

  const handleChange = React.useCallback(
    ({ value: inputValue, name, id, target }: any) => {
      const isRadioOrCheckbox = /radio|checkbox/.test(target?.type || '');

      if (isRadioOrCheckbox) {
        const formControl = marketoFormRef.current?.querySelector(
          `#${id.replace(/_2$/, '')}`,
        ) as HTMLInputElement;
        formControl?.click();

        setTimeout(() => {
          updateControls(form);
        }, 100);
        return;
      }

      setValue((prevValue: any) => {
        const newValue = { ...prevValue, [name]: inputValue };
        delayedUpdateMarketoForm(newValue);

        return newValue;
      });
    },
    [delayedUpdateMarketoForm, form, updateControls],
  );

  const applyAdvancedRules = useCallback(
    ({ value: newValue, controls: newControls }: any) => {
      // 0. AdvancedLogic algorithm to get empty fields.
      const fieldsHidden = advancedLogicFacade({ advancedRules, value: newValue });

      // 1. Filter HTML elements.
      const filteredFields = advancedLogicFilterFields({
        ids: fieldsHidden,
        controls: newControls,
      });
      setRenderableControls(filteredFields);

      // 2. Update EmptyFields State as an object with empty values.
      const filteredValues = advancedLogicEmptyFieldsValue(fieldsHidden);
      setEmptyFields(filteredValues);
    },
    [advancedRules],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedApplyAdvancedRules = useCallback(
    debounce((args: any) => applyAdvancedRules(args), DEBOUNCE_TIMEOUT_MILISECONDS.current),
    [],
  );

  useEffect(() => {
    // No Advanced rules mean all controls renderable.
    if (!advancedRules.length) {
      setRenderableControls(controls);
      return;
    }

    if (!controls || !value || !advancedRules.length) {
      return;
    }

    delayedApplyAdvancedRules({ value, controls });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, controls]);

  const messageBarProps = useMemo(
    () => ({
      isOpen: messageBarOpen,
      onClose: handleCloseMessageBar,
      message: confirmationMessage,
      autoHideDuration: null,
      hasOverlay: true,
    }),
    [confirmationMessage, handleCloseMessageBar, messageBarOpen],
  );

  const formClassName = useMemo(
    () =>
      clsx(
        classes.form,
        `bg-color-${appearance?.backgroundColor ? appearance?.backgroundColor : containerColor}`,
        outline && 'is-outline',
      ),
    [appearance, classes.form, containerColor, outline],
  );

  const rootClassName = clsx(classes.root, `${appearanceTheme}-theme`, 'form');
  const preloaderClassName = clsx(classes.preloader);

  const lastActiveFieldNameRef = React.useRef('');
  const [triggerDataLayerErrorEvent, setTriggerDataLayerErrorEvent] = React.useState(false);

  React.useEffect(() => {
    if (!triggerDataLayerErrorEvent) return;

    const errorControl = renderableControls
      ?.find((row) => !!row?.find((control: any) => !!control?.error))
      ?.find((control: any) => !!control?.error);
    if (!errorControl?.error) return;
    datalayerFormErrorEvent(form?.getValues(), errorControl);
    setTriggerDataLayerErrorEvent(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, heading, triggerDataLayerErrorEvent, renderableControls, datalayerFormErrorEvent]);

  React.useEffect(() => {
    if (!form) return;

    form.onValidate((isValid: boolean) => {
      if (isValid) return;

      setTriggerDataLayerErrorEvent(() => false);
      setTimeout(() => {
        setTriggerDataLayerErrorEvent(() => true);
      }, 500);
    });

    const formValues = form?.getValues();
    if (setParentDataLayerFormAttributes)
      setParentDataLayerFormAttributes((prev) => ({
        ...prev,
        formName: formValues?.formName,
        formType: formValues?.formType,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  React.useEffect(() => {
    if (!isOpenedByCTAVariant) return;
    if (!form) return;

    datalayerClickToFormEvent(form?.getValues(), isOpenedByCTAVariant, datalayerLinkText);
  }, [formId, isOpenedByCTAVariant, datalayerLinkText, form, datalayerClickToFormEvent]);

  const handleFocus = React.useCallback(
    (e: FocusEvent<HTMLFormElement>) => {
      const targetId = e?.target?.id;

      let fieldName = targetId?.split('_')?.[0] || '';
      if (targetId.includes(SELECT_FORM_CONTROL_PREFIX))
        fieldName = targetId.replace(SELECT_FORM_CONTROL_PREFIX, '');
      if (targetId.includes(CHECKBOX_FORM_CONTROL_PREFIX))
        fieldName = e?.target?.getAttribute('name') || '';

      if (!fieldName) return;
      lastActiveFieldNameRef.current = fieldName;
      dataLayerFocusEvent(fieldName, form?.getValues());
    },
    [dataLayerFocusEvent, form],
  );

  return (
    <section
      data-testid="qa-form-component"
      className={clsx(rootClassName, overrideClassName, formAppearance)}
      ref={containerRef}
    >
      {contentButtons?.content}
      {form && heading && (
        <MuiTypography
          variant="h2"
          component="h2"
          data-testid="qa-form-heading"
          className={classes.formHeading}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
      )}
      {form && formDescription && (
        <CaptureClicksRichText
          content={formDescription}
          containerProps={{ className: classes.formDescription }}
          theme={appearanceTheme}
        />
      )}
      <form
        ref={marketoFormRef}
        className={clsx(classes.marketoForm, 'mktoForm')}
        aria-hidden="true"
        onFocus={handleFocusHiddenForm}
        data-form={formId}
      />
      {form && renderableControls.length > 0 && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={clsx(formClassName)}
          onFocus={handleFocus}
        >
          <MarketoRow
            theme={appearanceTheme}
            controls={renderableControls}
            onChange={handleChange}
            value={value}
            classes={classes}
            controlsDistribution={controlsDistribution}
            marketoInstance={form}
          />
          {recaptchaSitekey && (
            <MarketoReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              badge="bottomleft"
              legal={recaptchaLegal}
              sitekey={recaptchaSitekey}
              classes={classes}
              theme={appearanceTheme}
              setRecaptchaFlag={setRecaptchaFlag}
            />
          )}
        </form>
      )}
      {!form && (
        <div
          className={clsx(classes.preloaderContainer, (heading || formDescription) && 'has-text')}
        >
          <div className={preloaderClassName}>
            <MuiCircularProgress size="4rem" color="inherit" />
          </div>
        </div>
      )}

      {displayMessages && confirmationMessage && (
        <MessageBar {...messageBarProps} message={confirmationMessage} severity="success" />
      )}
      {!!emailBlacklistErrorMessage && <MessageBar {...errorMessageBarProps} severity="error" />}
    </section>
  );
};

const MarketoFormWithGTM = withGTM({
  Component: memo(MarketoFormComponent),
  onDatalayer: onDatalayerCustomSubmit,
  config: {
    eventName: 'gtm:custom:form:submit',
    isCustom: true,
  },
});

export default MarketoFormWithGTM;
